import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { shallow } from 'zustand/shallow';

import { CreateMemoryGameContentListTemplate } from '../../components/Templates';
import { getLanguageDetail } from '../../constants/languageDetails';
import { QuickCreateGameMode } from '../../models/DraftStore';
import { useDraftStore } from '../../stores/DraftStore';
import { arrayEqual } from '../../utils/array';
import { isNotEmptyString } from '../../utils/string';

interface State {
  createType: QuickCreateGameMode | undefined;
  gameContents: {
    question: string;
    answer: string;
    questionPlaceHolder: string;
    answerPlaceHolder: string;
    questionHasTts: boolean;
    answerHasTts: boolean;
  }[];
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  showRequiredWarning: boolean;
}

const CreateMemoryGameContentListScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({
    createType: undefined,
    gameContents: [],
    isNextDisabled: false,
    isExceedLimit: false,
    showRequiredWarning: false,
  });

  const { mode, locale } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    const _locale = store.getLocale();
    return { mode: _mode, locale: _locale };
  });

  const drafts = useDraftStore((store) => {
    return store.getGameDrafts();
  }, arrayEqual(shallow));

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    if (locale) {
      setState((s) => ({ ...s, selectedLanguage: locale }));
    }
  }, [locale]);

  useEffect(() => {
    if (drafts && drafts.length > 0) {
      const gameContents = drafts.map((draft) => {
        return {
          question: draft.game.question.text?.text ?? '',
          answer: draft.game.answer.text?.text ?? '',
          questionPlaceHolder: `<${t(
            getLanguageDetail(locale)?.displayName ?? '',
          )}>\n${t(draft.game.question.text?.placeholder ?? '')}`,
          answerPlaceHolder: `<${t(
            getLanguageDetail(locale)?.displayName ?? '',
          )}>\n${t(draft.game.answer.text?.placeholder ?? '')}`,
          questionHasTts: draft.game.question.hasTts,
          answerHasTts: draft.game.answer.hasTts,
        };
      });
      setState((s) => ({ ...s, gameContents: gameContents }));
    }
  }, [drafts, drafts.length, locale, t]);

  useEffect(() => {
    const handlePageShow = () => {
      navigate('/create/memory-game/processed-content', { replace: true });
    };

    window.addEventListener('popstate', handlePageShow);

    return () => {
      window.removeEventListener('popstate', handlePageShow);
    };
  }, [navigate]);

  const onClickRemove = useCallback((index: number) => {
    useDraftStore.getState().removeGameDraft(index);
  }, []);

  const onClickAddContent = useCallback(() => {
    useDraftStore.getState().addGameDraft();
  }, []);

  const onClickNext = useCallback(() => {
    const hasEmpty = state.gameContents.some(
      ({ question, answer }) =>
        !isNotEmptyString(question) || !isNotEmptyString(answer),
    );
    if (hasEmpty) {
      setState((s) => ({ ...s, showRequiredWarning: true }));
    } else {
      setState((s) => ({ ...s, showRequiredWarning: false }));
      useDraftStore.getState().convertDraftsToSelectWords();
      navigate('/create/memory-game/select-words', { replace: true });
    }
  }, [navigate, state.gameContents]);

  const onClickEdit = useCallback(
    (index: number, type: string) => {
      navigate('/create/memory-game/edit-game', {
        replace: true,
        state: { index: index, type: type },
      });
    },
    [navigate],
  );

  const onClickBack = useCallback(() => {
    navigate('/create/memory-game/processed-content', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (state.gameContents.length > 20) {
      setState((s) => ({ ...s, isNextDisabled: true, isExceedLimit: true }));
    } else {
      setState((s) => ({ ...s, isNextDisabled: false, isExceedLimit: false }));
    }
  }, [locale, state.gameContents]);

  const onClickAudioBar = useCallback(() => {
    toast.success(
      t(`Listen to the text-to-speech when playing the created game.`),
    );
  }, [t]);

  return (
    <CreateMemoryGameContentListTemplate
      type={state.createType}
      gameContents={state.gameContents}
      isNextDisabled={state.isNextDisabled}
      isExceedLimit={state.isExceedLimit}
      showRequiredWarning={state.showRequiredWarning}
      onClickEdit={onClickEdit}
      onClickRemove={onClickRemove}
      onClickAddContent={onClickAddContent}
      onClickAudioBar={onClickAudioBar}
      onClickNext={onClickNext}
      onClickBack={onClickBack}
    />
  );
};
export default CreateMemoryGameContentListScreen;
